import React, { useEffect, useRef } from 'react';

// Two-layer parallax starfield, tuned to look like actual points of
// light: small radii, mostly white/pale-blue, only a rare and very
// faint color tint — not big saturated colored balls.
const Starfield = ({ density = 3000 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let stars = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      stars = Array.from({ length: density }, () => {
        const far = Math.random() > 0.3; // most stars are "far" = tiny + dim
        const roll = Math.random();
        // ~85% plain white, ~15% a very faint cyan/pink tint
        const rgb = roll > 0.93 ? '247, 200, 220' : roll > 0.86 ? '190, 225, 245' : '255, 255, 255';
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: far ? Math.random() * 0.6 + 0.2 : Math.random() * 0.9 + 0.5,
          speed: far ? Math.random() * 0.05 + 0.025 : Math.random() * 0.16 + 0.1,
          tw: Math.random() * Math.PI * 2,
          twSpeed: Math.random() * 0.015 + 0.008,
          rgb,
          far,
        };
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        s.tw += s.twSpeed;
        const base = s.far ? 0.25 : 0.45;
        const alpha = base + Math.sin(s.tw) * base * 0.8;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.rgb}, ${Math.max(alpha, 0.05)})`;
        ctx.fill();
        s.y += s.speed;
        if (s.y > canvas.height) {
          s.y = 0;
          s.x = Math.random() * canvas.width;
        }
      }
      animationId = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default Starfield;